import { useState, useEffect, useRef, useContext } from 'react';
import { useIntl } from 'react-intl';
import { message, theme } from 'antd';

import { UserContext } from './context'
import { checkPermission } from './utils'

export const useReactPath = () => {
  const [path, setPath] = useState(window.location.pathname)
  const listenToPopstate = () => {
    const winPath = window.location.pathname
    setPath(winPath)
  };
  useEffect(() => {
    window.addEventListener("popstate", listenToPopstate)
    return () => {
      window.removeEventListener("popstate", listenToPopstate)
    }
  }, [])
  return path
};

/**
 *  @param {id} Message id
 *  @param {values} Values object of the formatted message
 */
export const useFormatMessage = () => {
  const { formatMessage } = useIntl()
  return (id, values) => formatMessage({ id }, values)
}

/**
 * Translates errorCode from API
 * logic:
 ** if there is a translatable errorCode, there should be no errorMessage from the server
 ** if there is an errorMessage, show it first, because there is no translatable error code
 *  @param {response} object (error.response)
 */
const translateApiError = (response, f, name = 'this request') => {
  if (!response) return f('Error.Unknown')
  if (response.status === 403) return `Permissions missing for ${name}`
  const { data } = response
  if (typeof data === 'string') return data
  if (data?.errorMessage) return data.errorMessage
  if (data?.errorCode) return f(data.errorCode)
  return `${f('Error')}: ${response.status} ${response.statusText}`
}

export const useHandleApiError = () => {
  const f = useFormatMessage()

  return (error, name = 'this request') => {
    const { response } = error

    if (!response) {
      if (process.env.NODE_ENV === 'development') {
        console.log("coding error", error)
      }
    }

    if (response?.status === 401) {
      // we do not return an error here because it is (usually) transient
      console.log("User is not authenticated")
      return
    }

    const text = translateApiError(response, f, name)
    message.error(text)
    if (process.env.NODE_ENV === 'development') {
      console.log(text)
      console.log(response)
    }
  }
}

/** return current Ant design theme - 0: light, 1: dark */
export const useTheme = () => {
  const token = theme.useToken()
  return token.theme.id === 0 ? 'light' : 'dark'
}

/**
 * author: Den Abramov
 * https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 */
export function useInterval(callback, delay) {
  const savedCallback = useRef()

  // remember the latest callback
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  // set up the interval
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }
    const id = setInterval(tick, delay)
    return () => clearInterval(id)
  }, [delay])
}

export function useHavePermission() {
  const { permissions } = useContext(UserContext)

  return permission => checkPermission(permissions, permission)
}
